import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { CSSProperties } from "@material-ui/styles";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import useUser from "@saleor/hooks/useUser";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage } from "react-intl";
import Moment from "react-moment";

import { Payout_payouts_edges_node } from "../../types/Payout";
import { selectedRow } from "../../utils";
const useStyles = makeStyles(
  theme => {
    const overflowing: CSSProperties = {
      overflow: "hidden",
      textOverflow: "ellipsis"
    };
    return {
      clickRow: {
        cursor: "pointer"
      },
      colMedium: {
        overflowing,
        width: "9em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colNarrow: {
        overflowing,
        width: "8em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colRow: {
        "& th": {
          fontWeight: 600 as 600,
          paddingBottom: theme.spacing(1),
          paddingTop: theme.spacing(1)
        }
      },
      colWide: {
        overflowing,
        width: "12em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      table: {
        "& td": {
          fontSize: "15px"
        },
        "& th": {
          fontSize: "15px"
        },
        tableLayout: "fixed"
      }
    };
  },
  { name: "PayoutsList" }
);

interface PayoutsListProp extends ListProps {
  payouts: Payout_payouts_edges_node[];
}

const PayoutsList: React.FC<PayoutsListProp> = ({
  payouts,
  onNextPage,
  onPreviousPage,
  onRowClick,
  onUpdateListSettings,
  pageInfo,
  settings,
  disabled
}) => {
  const colNum = 6;
  const classes = useStyles({});
  const { user } = useUser();

  return (
    <>
      <ResponsiveTable className={classes.table}>
        <TableHead>
          <TableCellHeader className={classes.colMedium}>
            <FormattedMessage defaultMessage="Payout ID" />
          </TableCellHeader>
          <TableCellHeader className={classes.colWide}>
            <FormattedMessage defaultMessage="Mall" />
          </TableCellHeader>
          <TableCellHeader className={classes.colWide}>
            <FormattedMessage defaultMessage="Brand" />
          </TableCellHeader>
          <TableCellHeader className={classes.colWide}>
            <FormattedMessage defaultMessage="Period" />
          </TableCellHeader>
          <TableCellHeader className={classes.colWide}>
            <FormattedMessage defaultMessage="Date Generated" />
          </TableCellHeader>
          <TableCellHeader className={classes.colMedium}>
            <FormattedMessage defaultMessage="Payout Status" />
          </TableCellHeader>
        </TableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={colNum}
              settings={settings}
              hasNextPage={
                pageInfo && !disabled ? pageInfo.hasNextPage : undefined
              }
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : undefined
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
        <TableBody>
          {payouts &&
            renderCollection(
              payouts,
              payout => (
                <TableRow
                  className={classes.clickRow}
                  hover={!!payout}
                  key={payout ? payout.id : "skeleton"}
                  onClick={
                    selectedRow(user.userPermissions, payout.payout_status)
                      ? onRowClick(payout.id)
                      : undefined
                  }
                >
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => atob(payout.id),
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => payout.mall_name,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => payout.brand_name,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    <Moment format="YYYY-MM-DD hh:mm" tz="Asia/Manila">
                      {payout?.coverage_start_date ? (
                        payout.coverage_start_date
                      ) : (
                        <Skeleton />
                      )}
                    </Moment>
                    <FormattedMessage defaultMessage="  to  " />
                    <Moment format="YYYY-MM-DD hh:mm " tz="Asia/Manila">
                      {payout?.coverage_end_date ? (
                        payout.coverage_end_date
                      ) : (
                        <Skeleton />
                      )}
                    </Moment>
                  </TableCell>

                  <TableCell>
                    <Moment format="YYYY-MM-DD hh:mm">
                      {payout?.generated_at ? (
                        payout.generated_at
                      ) : (
                        <Skeleton />
                      )}
                    </Moment>
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => payout.payout_status,
                      <Skeleton />
                    )}
                  </TableCell>
                </TableRow>
              ),
              () => (
                <TableRow>
                  <TableCell colSpan={colNum}>
                    <FormattedMessage defaultMessage="No payouts found" />
                  </TableCell>
                </TableRow>
              )
            )}
        </TableBody>
      </ResponsiveTable>
    </>
  );
};

export default PayoutsList;
